import type { VistaCard } from "../contratos";
import { useActionState } from "react";
import { Link, useNavigate } from "react-router";

import { campo } from "../api/cliente";
import { compartilhar, contestar } from "../api/prisma";
import estilos from "./card.module.css";
import { caminho } from "./util";

export function Rodape(props: {
  vista: VistaCard;
  numero: number;
  textoUrl: string;
  recarregar: () => void;
}) {
  const { vista, textoUrl } = props;
  return (
    <footer className={estilos.rodape}>
      <section className={estilos.bloco} aria-labelledby={`b${props.numero}`}>
        <h2 id={`b${props.numero}`}>{props.numero}. Sobre esta análise</h2>
        <p>{vista.como_ler_pagina}</p>
        <p>{vista.sobre}</p>
        {/* A ficha técnica em grade: rótulo em cima, valor embaixo; duas colunas se couber. */}
        <dl className={estilos.ficha}>
          <div>
            <dt>Júri</dt>
            <dd>{vista.placar}</dd>
          </div>
          <div>
            <dt>Conferência de citações</dt>
            <dd>{vista.conferencia}</dd>
          </div>
          {vista.perfil && (
            <div>
              <dt>Perfil de modelos</dt>
              <dd>{vista.perfil}.</dd>
            </div>
          )}
          {vista.modelos.length > 0 && (
            <div>
              <dt>Modelos usados</dt>
              <dd>{vista.modelos.join("; ")}.</dd>
            </div>
          )}
          <div>
            <dt>Data da análise</dt>
            <dd>
              {vista.data} (versão {vista.versao}).
            </dd>
          </div>
          <div>
            <dt>Impressão digital do dossiê</dt>
            <dd className={estilos.hash}>{vista.hash}</dd>
          </div>
          {(vista.composicao ?? []).length > 0 && (
            <div className={estilos.fichaLarga}>
              <dt>Quem fez cada etapa</dt>
              <dd>
                <ul className={estilos.etapas}>
                  {(vista.composicao ?? []).map((e) => (
                    <li key={e.etapa}>
                      <span>{e.etapa}</span>
                      <span>
                        {e.modelo}
                        {e.esforco ? `, esforço ${e.esforco}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
                {vista.composicao_aviso && (
                  <p className={estilos.aviso}>{vista.composicao_aviso}</p>
                )}
              </dd>
            </div>
          )}
        </dl>
        <nav aria-label="Este card em outros lugares" className={estilos.links}>
          <Link to={caminho(vista.url_canonica)} title={vista.url_canonica}>
            <span aria-hidden="true">🔗</span> Link deste card
          </Link>
          <a href={textoUrl}>
            <span aria-hidden="true">≡</span> Este card em texto
          </a>
          {vista.url_recibo && (
            <Link to={caminho(vista.url_recibo)}>
              <span aria-hidden="true">🧾</span> Recibo completo da análise
            </Link>
          )}
        </nav>
        {vista.contestacoes.length > 0 && (
          <>
            <h3 className={estilos.subtitulo}>Contestações registradas</h3>
            <ul>
              {vista.contestacoes.map((c, i) => (
                <li key={`${c.data}-${i}`}>
                  {c.data}: {c.argumento}
                </li>
              ))}
            </ul>
          </>
        )}
        {(vista.url_compartilhar ?? vista.url_contestar) && (
          <div className={estilos.acoes}>
            {vista.url_compartilhar && <Compartilhar url={vista.url_compartilhar} />}
            {vista.url_contestar && (
              <Contestar url={vista.url_contestar} recarregar={props.recarregar} />
            )}
          </div>
        )}
      </section>
    </footer>
  );
}

function Compartilhar({ url }: { url: string }) {
  const navegar = useNavigate();
  const [erro, enviar, enviando] = useActionState(async () => {
    try {
      const { url_publica } = await compartilhar(url);
      await navegar(url_publica);
      return null;
    } catch (e) {
      return e instanceof Error ? e.message : "Algo deu errado.";
    }
  }, null);
  return (
    <form className={estilos.acao} action={enviar}>
      <h3 className={estilos.subtitulo}>Compartilhar</h3>
      <p>Ao compartilhar, o card e o recibo ganham um link público. Isso não pode ser desfeito.</p>
      {erro && (
        <p className="erro" role="alert">
          {erro}
        </p>
      )}
      <button type="submit" disabled={enviando}>
        Compartilhar este card
      </button>
    </form>
  );
}

type Envio = { ok: true } | { ok: false; erro: string } | null;

function Contestar({ url, recarregar }: { url: string; recarregar: () => void }) {
  const [envio, enviar, enviando] = useActionState(
    async (_: Envio, dados: FormData): Promise<Envio> => {
      try {
        await contestar(url, campo(dados, "argumento"), campo(dados, "link_evidencia").trim());
      } catch (e) {
        return { ok: false, erro: e instanceof Error ? e.message : "Algo deu errado." };
      }
      recarregar();
      return { ok: true };
    },
    null,
  );
  if (envio?.ok) {
    return (
      <p className={estilos.acao} role="status">
        Contestação registrada. A análise é refeita e a mudança fica registrada nesta página.
      </p>
    );
  }
  return (
    <details className={estilos.acao} open={envio !== null}>
      <summary>Encontrou um erro?</summary>
      <form action={enviar}>
        {envio && (
          <p className="erro" role="alert">
            {envio.erro}
          </p>
        )}
        <label htmlFor="argumento">Encontrou um erro? Conte o que está errado:</label>
        <textarea id="argumento" name="argumento" rows={3} maxLength={5000} required />
        <label htmlFor="link_evidencia">Link para a evidência (opcional):</label>
        <input
          id="link_evidencia"
          name="link_evidencia"
          type="url"
          inputMode="url"
          maxLength={500}
        />
        <button type="submit" disabled={enviando}>
          Contestar
        </button>
      </form>
    </details>
  );
}
